import { makeStyles, tokens } from '@fluentui/react-components';
import { useNavigate } from 'react-router';

import { ConfigRail } from '../../components/caller/ConfigRail/ConfigRail.js';
import { RequestPanel } from '../../components/caller/RequestPanel/RequestPanel.js';
import { ResponsePanel } from '../../components/caller/ResponsePanel/ResponsePanel.js';
import { UrlBar } from '../../components/caller/UrlBar/UrlBar.js';
import { useApiCall } from '../../hooks/useApiCall/useApiCall.js';
import { useCallerStore } from '../../store/callerStore.js';
import type { SavedConfig } from '../../types/caller.js';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    height: '100%',
    overflow: 'hidden',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  main: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden',
    minWidth: 0,
  },
  splitPane: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
  },
});

export function CallerPage() {
  const styles = useStyles();
  const { state, dispatch } = useCallerStore();
  const navigate = useNavigate();
  const { loading, varErrors, execute, cancel } = useApiCall();

  const activeId = state.activeTabId;
  const workingConfig = activeId ? state.workingConfigs[activeId] : null;

  function buildActiveConfig(): SavedConfig | null {
    if (!activeId || !workingConfig) return null;
    const saved = state.savedConfigs.find((c) => c.id === activeId);
    return {
      id: activeId,
      name: workingConfig.name,
      url: workingConfig.url,
      method: workingConfig.method,
      headers: workingConfig.headers,
      params: workingConfig.params,
      vars: workingConfig.vars,
      body: workingConfig.body,
      createdAt: saved?.createdAt ?? new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  }

  function handleOpenConfig(configId: string) {
    dispatch({ type: 'OPEN_TAB', configId });
  }

  function handleNewConfig() {
    dispatch({ type: 'ADD_NEW_TAB' });
  }

  function handleDeleteConfig(configId: string) {
    dispatch({ type: 'DELETE_CONFIG', id: configId });
  }

  function handleRenameConfig(configId: string, name: string) {
    dispatch({ type: 'RENAME_CONFIG', id: configId, name });
  }

  function handleCloneConfig(configId: string) {
    dispatch({ type: 'CLONE_CONFIG', id: configId });
  }

  function handleSave() {
    if (!activeId || !workingConfig) return;
    const existing = state.savedConfigs.find((c) => c.id === activeId);
    const updated: SavedConfig = {
      id: activeId,
      name: workingConfig.name,
      url: workingConfig.url,
      method: workingConfig.method,
      headers: workingConfig.headers,
      params: workingConfig.params,
      vars: workingConfig.vars,
      body: workingConfig.body,
      createdAt: existing?.createdAt ?? new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    dispatch({ type: 'SAVE_CONFIG', config: updated });
  }

  async function handleRun() {
    const config = buildActiveConfig();
    if (!config) return;
    const res = await execute(config, state.emitSigsLocally);
    if (res && activeId) {
      dispatch({
        type: 'SET_RESPONSE',
        configId: activeId,
        response: {
          status: res.status,
          statusText: res.statusText,
          headers: res.headers,
          body: res.body,
          durationMs: res.durationMs,
          cv: res.cv,
        },
      });
    }
  }

  function handleDebug(cv: string) {
    void navigate('/debug?cv=' + encodeURIComponent(cv));
  }

  return (
    <div className={styles.root}>
      <ConfigRail
        savedConfigs={state.savedConfigs}
        activeTabId={state.activeTabId}
        onOpenConfig={handleOpenConfig}
        onNewConfig={handleNewConfig}
        onDeleteConfig={handleDeleteConfig}
        onRenameConfig={handleRenameConfig}
        onCloneConfig={handleCloneConfig}
      />

      <div className={styles.main}>
        <UrlBar
          openTabs={state.openTabs}
          activeTabId={state.activeTabId}
          savedConfigs={state.savedConfigs}
          activeConfig={
            workingConfig
              ? {
                  url: workingConfig.url,
                  method: workingConfig.method,
                  name: workingConfig.name,
                  vars: workingConfig.vars,
                }
              : null
          }
          emitSigsLocally={state.emitSigsLocally}
          loading={loading}
          onTabSelect={(configId) =>
            dispatch({ type: 'SET_ACTIVE_TAB', configId })
          }
          onCloseTab={(configId) => dispatch({ type: 'CLOSE_TAB', configId })}
          onNewTab={handleNewConfig}
          onUrlChange={(url) =>
            activeId &&
            dispatch({
              type: 'UPDATE_WORKING_CONFIG',
              configId: activeId,
              patch: { url },
            })
          }
          onMethodChange={(method) =>
            activeId &&
            dispatch({
              type: 'UPDATE_WORKING_CONFIG',
              configId: activeId,
              patch: { method },
            })
          }
          onSave={handleSave}
          onRun={() => void handleRun()}
          onToggleEmitSigs={(value) =>
            dispatch({ type: 'SET_EMIT_SIGS', value })
          }
        />

        <div className={styles.splitPane}>
          {workingConfig ? (
            <RequestPanel
              params={workingConfig.params}
              body={workingConfig.body}
              headers={workingConfig.headers}
              vars={workingConfig.vars}
              onParamsChange={(params) =>
                activeId &&
                dispatch({
                  type: 'UPDATE_WORKING_CONFIG',
                  configId: activeId,
                  patch: { params },
                })
              }
              onBodyChange={(body) =>
                activeId &&
                dispatch({
                  type: 'UPDATE_WORKING_CONFIG',
                  configId: activeId,
                  patch: { body },
                })
              }
              onHeadersChange={(headers) =>
                activeId &&
                dispatch({
                  type: 'UPDATE_WORKING_CONFIG',
                  configId: activeId,
                  patch: { headers },
                })
              }
              onVarsChange={(vars) =>
                activeId &&
                dispatch({
                  type: 'UPDATE_WORKING_CONFIG',
                  configId: activeId,
                  patch: { vars },
                })
              }
            />
          ) : (
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: tokens.colorNeutralForeground3,
              }}
            >
              Open a saved request or create a new one
            </div>
          )}

          <ResponsePanel
            result={activeId ? (state.responses[activeId] ?? null) : null}
            loading={loading}
            varErrors={varErrors}
            onDebug={handleDebug}
            onCancel={cancel}
          />
        </div>
      </div>
    </div>
  );
}
